import { action, observable, makeObservable, runInAction } from "mobx";

import {
  PartnerCustomerInventoryTag,
  PartnerCustomerInventoryTagUpdateRequest,
} from "../../../models/inventory/PartnerCustomerInventoryTag";
import PartnerCustomerInventoryTagsAPI from "../api/PartnerCustomerInventoryTagsAPI";

export class PartnerCustomerInventoryTagsStore {
  partnerCustomerInventoryTags: PartnerCustomerInventoryTag[] = [];
  partnerCustomerInventoryTagsAPI: PartnerCustomerInventoryTagsAPI;

  private lastCustomerId: number | null = null;
  private lastShowLooseItems: boolean | undefined = undefined;

  constructor() {
    this.partnerCustomerInventoryTags = [];
    this.partnerCustomerInventoryTagsAPI =
      new PartnerCustomerInventoryTagsAPI();

    makeObservable(this, {
      partnerCustomerInventoryTags: observable,
      getPartnerCustomerInventoryTagsByCustomerId: action,
      createTag: action,
      updateTag: action,
      deleteTag: action,
    });
  }

  getPartnerCustomerInventoryTagsByCustomerId = async (
    customerId: number,
    showLooseItems?: boolean,
  ) => {
    try {
      this.lastCustomerId = customerId;
      this.lastShowLooseItems = showLooseItems;
      const response =
        await this.partnerCustomerInventoryTagsAPI.getTagsByCustomerId(
          customerId,
          showLooseItems,
        );
      runInAction(() => {
        this.partnerCustomerInventoryTags = response;
      });
    } catch (error) {
      throw error;
    }
  };

  getTag = async (customerId: number, tagId: string) => {
    try {
      return await this.partnerCustomerInventoryTagsAPI.getTag(
        customerId,
        tagId,
      );
    } catch (error) {
      throw error;
    }
  };

  /** Creates the tag, refetches the list and returns the created tag. */
  createTag = async (
    customerId: number,
    tagId: string,
    tag: string,
  ): Promise<PartnerCustomerInventoryTag> => {
    try {
      await this.partnerCustomerInventoryTagsAPI.createTag({
        customer_id: customerId,
        tag_id: tagId.trim(),
        tag: tag.trim(),
      });
      await this.refetch(customerId);
      return await this.partnerCustomerInventoryTagsAPI.getTag(
        customerId,
        tagId.trim(),
      );
    } catch (error) {
      throw error;
    }
  };

  updateTag = async (
    customerId: number,
    tagId: string,
    body: PartnerCustomerInventoryTagUpdateRequest,
  ) => {
    try {
      await this.partnerCustomerInventoryTagsAPI.updateTag(
        customerId,
        tagId,
        body,
      );
    } finally {
      await this.refetch(customerId);
    }
  };

  /** Refetches even on failure: a 404 means the tag was already removed. */
  deleteTag = async (customerId: number, tagId: string) => {
    try {
      await this.partnerCustomerInventoryTagsAPI.deleteTag(customerId, tagId);
    } finally {
      await this.refetch(customerId);
    }
  };

  private refetch = async (customerId: number) => {
    const showLooseItems =
      this.lastCustomerId === customerId ? this.lastShowLooseItems : undefined;
    await this.getPartnerCustomerInventoryTagsByCustomerId(
      customerId,
      showLooseItems,
    );
  };
}
